import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { MdAccessTime, MdCheckCircle, MdChevronRight, MdCode, MdExpandMore } from 'react-icons/md';
import { setPageSEO } from '../utils/seo.js';
import { getRouteMetadata, integrationPlatforms } from '../utils/seoMetadata.js';
import './IntegrationDocumentation.css';

const integrationRows = [
  { platform: 'WordPress', method: 'Custom HTML block/plugin/theme', difficulty: 'Easy', slug: 'wordpress' },
  { platform: 'Shopify', method: 'Custom Liquid section', difficulty: 'Easy', slug: 'shopify' },
  { platform: 'Webflow', method: 'Embed block', difficulty: 'Easy', slug: 'webflow' },
  { platform: 'Wix', method: 'Custom Embed', difficulty: 'Easy', slug: 'wix' },
  { platform: 'Squarespace', method: 'Code Injection', difficulty: 'Easy', slug: 'squarespace' },
  { platform: 'BigCommerce', method: 'Script Manager / HTML widget', difficulty: 'Easy', slug: 'bigcommerce' },
  { platform: 'Magento', method: 'CMS Block / Layout XML', difficulty: 'Medium', slug: 'magento' },
  { platform: 'Drupal', method: 'Custom Block', difficulty: 'Medium', slug: 'drupal' },
  { platform: 'Joomla', method: 'Custom HTML Module', difficulty: 'Easy', slug: 'joomla' },
  { platform: 'HubSpot', method: 'Custom Module / Embed', difficulty: 'Easy', slug: 'hubspot' },
  { platform: 'Ghost', method: 'HTML card', difficulty: 'Easy', slug: 'ghost' },
  { platform: 'Framer', method: 'Embed Component', difficulty: 'Easy', slug: 'framer' },
  { platform: 'Duda', method: 'HTML widget', difficulty: 'Easy', slug: 'duda' },
  { platform: 'Bubble', method: 'HTML Element', difficulty: 'Easy', slug: 'bubble' },
  { platform: 'Carrd', method: 'Embed element', difficulty: 'Easy', slug: 'carrd' },
  { platform: 'Notion', method: 'Embed block (limited)', difficulty: 'Medium', slug: 'notion' },
  { platform: 'Next.js', method: 'Script component', difficulty: 'Easy', slug: 'nextjs' },
  { platform: 'React', method: 'useEffect script inject', difficulty: 'Easy', slug: 'react' },
  { platform: 'Angular', method: 'Renderer2/script injection', difficulty: 'Easy', slug: 'angular' },
  { platform: 'Vue.js', method: 'Mounted hook script', difficulty: 'Easy', slug: 'vuejs' },
];

const docsUrlMap = Object.fromEntries(
  integrationRows.map((row) => [
    row.slug,
    `/documentation/integration/${row.slug}/index.html`,
  ])
);

const IntegrationDocumentation = () => {
  const location = useLocation();
  const { platform } = useParams();
  const iframeRef = useRef(null);
  const [treeExpanded, setTreeExpanded] = useState(true);
  const [iframeHeight, setIframeHeight] = useState('100vh');

  const isPreview = new URLSearchParams(location.search).get('preview') === '1';
  const isPlatformPage = integrationRows.some((row) => row.slug === platform);
  const selectedPlatform = isPlatformPage ? platform : integrationRows[0].slug;
  const selectedRow = integrationRows.find((row) => row.slug === selectedPlatform) || integrationRows[0];
  const selectedIntegration = integrationPlatforms.find((item) => item.slug === selectedPlatform) || integrationPlatforms[0];
  const selectedDocUrl = docsUrlMap[selectedPlatform] || '';

  useEffect(() => setPageSEO(getRouteMetadata(location.pathname)), [location.pathname]);

  const resizeIframe = useCallback(() => {
    const iframe = iframeRef.current;
    const iframeDocument = iframe?.contentDocument;

    if (!iframeDocument) {
      return;
    }

    const height = Math.max(
      iframeDocument.documentElement.scrollHeight,
      iframeDocument.body?.scrollHeight || 0
    );

    setIframeHeight(`${height}px`);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', resizeIframe);

    return () => {
      window.removeEventListener('resize', resizeIframe);
    };
  }, [resizeIframe]);

  return (
    <div className={`integration-documentation-page${isPreview ? ' preview-mode' : ''}`}>
      {/* Header Section */}
      {!isPreview && (
        <header className="integration-doc-header">
          <div className="container">
            <nav className="integration-doc-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <MdChevronRight aria-hidden="true" />
              <Link to="/integration">Integrations</Link>
              {isPlatformPage && <><MdChevronRight aria-hidden="true" /><span>{selectedIntegration.name}</span></>}
            </nav>
            <div className="header-content">
              <div className="header-text">
                <span className="badge"><MdCode /> Developer guides</span>
                <h1>{isPlatformPage ? `${selectedIntegration.name} Testimonial Software` : 'Testimonial software integrations for your website'}</h1>
                <p>{isPlatformPage ? selectedIntegration.description : 'Choose your platform to collect customer feedback and publish testimonial widgets on your website.'}</p>
                <div className="header-ctas">
                  <a href="/signup" className="integration-doc-primary">Start collecting free <MdChevronRight /></a>
                  <Link to="/integration" className="integration-doc-secondary">Explore integrations</Link>
                </div>
              </div>
              <aside className="integration-doc-status" aria-label="Integration overview">
                <div className="integration-doc-status-bar"><span /><span /><span /><strong>clientalio / docs</strong></div>
                <div className="integration-doc-status-body">
                  <span className="integration-doc-status-label">QUICK START</span>
                  <h2>{integrationRows.length} platform guides</h2>
                  <p><MdCheckCircle /> Copy your widget code</p>
                  <p><MdCheckCircle /> Add it to your platform</p>
                  <p><MdCheckCircle /> Publish and auto-sync</p>
                  <div><MdAccessTime /> Most setups take under 5 minutes</div>
                </div>
              </aside>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className="integration-doc-content">
        <div className="container">
          {!isPlatformPage && !isPreview ? (
            <section aria-labelledby="integration-library-title">
              <div className="integration-library-heading">
                <span>Platform library</span>
                <h2 id="integration-library-title">Choose your website platform</h2>
                <p>Open a dedicated setup page for your website builder, CMS, ecommerce platform, or framework.</p>
              </div>
              <div className="integration-card-grid">
                {integrationPlatforms.map((integration) => {
                  const row = integrationRows.find((item) => item.slug === integration.slug);

                  return (
                    <Link className="integration-platform-card" to={`/integrations/${integration.slug}`} key={integration.slug}>
                      <div className="integration-platform-card-top">
                        <span className="integration-platform-mark" aria-hidden="true">{integration.name.charAt(0)}</span>
                        <span className={`difficulty difficulty-${row.difficulty.toLowerCase()}`}>{row.difficulty}</span>
                      </div>
                      <h3>{integration.name}</h3>
                      <p>{integration.description}</p>
                      <div className="integration-platform-card-footer">
                        <span><MdCode /> {row.method}</span>
                        <MdChevronRight aria-hidden="true" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ) : (
          <div className="doc-grid">
            {/* Left Column - Integration Tree */}
            <section className="integration-tree-section">
              <div className="platform-picker-copy">
                <span>Platform library</span>
                <h2>Choose your website</h2>
                <p>Select a platform to open its installation guide.</p>
              </div>
              <div className="tree-header">
                <button 
                  className="tree-toggle"
                  onClick={() => setTreeExpanded(!treeExpanded)}
                >
                  {treeExpanded ? <MdExpandMore /> : <MdChevronRight />}
                  <span>All platforms</span>
                </button>
              </div>

              {treeExpanded && (
                <div className="tree-content">
                  <ul className="platforms-tree">
                    {integrationRows.map((row) => {
                      const isSelected = selectedPlatform === row.slug;
                      return (
                        <li key={row.slug} className={isSelected ? 'active' : ''}>
                          <Link
                            className="tree-item"
                            to={`/integrations/${row.slug}`}
                          >
                            <span className="platform-name">{row.platform}</span>
                            <span className={`difficulty difficulty-${row.difficulty.toLowerCase()}`}>{row.difficulty}</span>
                            <MdChevronRight className="tree-icon" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </section>

            {/* Right Column - Documentation Preview */}
            <aside className="doc-preview-section">
              <div className="doc-preview-heading">
                <div>
                  <span>Installation guide</span>
                  <h2>{selectedRow.platform}</h2>
                </div>
                <p><MdCode /> {selectedRow.method}</p>
              </div>
              <div className="doc-preview-container">
                {selectedDocUrl ? (
                  <iframe
                    ref={iframeRef}
                    key={selectedDocUrl}
                    src={selectedDocUrl}
                    title={`${selectedRow.platform} documentation`}
                    className="doc-iframe"
                    style={{ height: iframeHeight }}
                    onLoad={resizeIframe}
                  />
                ) : (
                  <div className="doc-placeholder">
                    <p>Documentation page not yet available for this platform.</p>
                  </div>
                )}
              </div>

              <div className="tips-container">
                <div className="tip-card tip-best">
                  <p className="tip-label">Best practice</p>
                  <p className="tip-text">
                    Use the provided script embed in a secure HTML block or widget. This ensures your testimonials load consistently across platforms.
                  </p>
                </div>
                <div className="tip-card tip-advice">
                  <p className="tip-label">Tip</p>
                  <p className="tip-text">
                    Prefer the platform native embed/HTML module option for best styling compatibility.
                  </p>
                </div>
              </div>
            </aside>
          </div>
          )}
        </div>
      </div>
      {!isPreview && (
        <section className="integration-doc-cta">
          <div className="container">
            <div className="cta-inner">
              <div>
                <h2>Ready to embed testimonials on your site?</h2>
                <p>Create a branded collection page and publish your best customer stories in minutes.</p>
              </div>
              <div className="cta-actions">
                <a href="/signup" className="integration-doc-primary">Start free <MdChevronRight /></a>
                <Link to="/contact" className="integration-doc-cta-secondary">Contact sales</Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default IntegrationDocumentation;
